import { useEffect, useRef } from "react";
import { X, Send, Users, MessagesSquare, ArrowRight } from "lucide-react";

const TELEGRAM_URL = "https://t.me/m/kOAFX9g5M2Y1";
const COMMUNITY_URL =
    "https://chat.whatsapp.com/ICvMOjGpLCTJGeed07XrKk?s=cl&p=i&mlu=4&ilr=4";
const WHATSAPP_URL = "https://wa.me/19362623272";

const WhatsAppIcon = ({ className }) => (
    <svg viewBox="0 0 24 24" fill="currentColor" className={className} aria-hidden="true">
        <path d="M17.47 14.38c-.3-.15-1.76-.87-2.03-.97-.27-.1-.47-.15-.67.15-.2.3-.77.97-.94 1.17-.17.2-.35.22-.64.07-.3-.15-1.26-.46-2.39-1.48-.88-.79-1.48-1.76-1.65-2.06-.17-.3-.02-.46.13-.61.13-.13.3-.35.45-.52.15-.17.2-.3.3-.5.1-.2.05-.37-.03-.52-.07-.15-.67-1.61-.91-2.2-.24-.58-.49-.5-.67-.51h-.57c-.2 0-.52.07-.79.37-.27.3-1.04 1.02-1.04 2.48s1.07 2.88 1.21 3.08c.15.2 2.1 3.2 5.08 4.49.71.31 1.26.49 1.69.63.71.23 1.36.2 1.87.12.57-.09 1.76-.72 2.01-1.41.25-.7.25-1.29.17-1.41-.07-.13-.27-.2-.57-.35M12.05 21.78h-.01a9.87 9.87 0 0 1-5.03-1.38l-.36-.21-3.74.98 1-3.65-.24-.37a9.86 9.86 0 0 1-1.51-5.26c0-5.45 4.44-9.88 9.89-9.88 2.64 0 5.12 1.03 6.99 2.9a9.82 9.82 0 0 1 2.89 6.99c0 5.45-4.44 9.88-9.88 9.88m8.41-18.3A11.82 11.82 0 0 0 12.05 0C5.5 0 .16 5.33.16 11.89c0 2.1.55 4.14 1.59 5.94L.06 24l6.31-1.65a11.88 11.88 0 0 0 5.68 1.44h.01c6.55 0 11.89-5.33 11.89-11.89 0-3.18-1.24-6.16-3.49-8.41" />
    </svg>
);

export default function ContactModal({ open, onClose }) {
    const closeRef = useRef(null);

    useEffect(() => {
        if (!open) return undefined;
        const previousOverflow = document.body.style.overflow;
        document.body.style.overflow = "hidden";
        const onKeyDown = (event) => {
            if (event.key === "Escape") onClose();
        };
        window.addEventListener("keydown", onKeyDown);
        closeRef.current?.focus();
        return () => {
            document.body.style.overflow = previousOverflow;
            window.removeEventListener("keydown", onKeyDown);
        };
    }, [open, onClose]);

    if (!open) return null;

    return (
        <div
            data-testid="contact-modal-backdrop"
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/75 p-4 backdrop-blur-sm"
            onClick={onClose}
        >
            <div
                role="dialog"
                aria-modal="true"
                aria-labelledby="contact-modal-heading"
                aria-describedby="contact-modal-subtext"
                data-testid="contact-modal"
                className="relative max-h-[calc(100dvh-2rem)] w-full max-w-sm overflow-y-auto rounded-3xl border border-[#D4AF37]/45 bg-[#050505] px-5 pb-5 pt-6 shadow-[0_10px_48px_rgba(212,175,55,0.18)]"
                onClick={(event) => event.stopPropagation()}
            >
                <div
                    aria-hidden="true"
                    className="pointer-events-none absolute -top-16 left-1/2 h-40 w-56 -translate-x-1/2 rounded-full bg-[#D4AF37]/10 blur-3xl"
                />

                <button
                    type="button"
                    ref={closeRef}
                    data-testid="contact-modal-close-button"
                    onClick={onClose}
                    aria-label="Close contact popup"
                    className="absolute right-4 top-4 z-10 flex h-9 w-9 items-center justify-center rounded-full border border-[#D4AF37]/45 text-[#D4AF37] transition-colors duration-200 hover:bg-[#D4AF37]/10 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#D4AF37]"
                >
                    <X className="h-5 w-5" strokeWidth={2} aria-hidden="true" />
                </button>

                <div className="pointer-events-none relative flex justify-center" aria-hidden="true">
                    <span className="flex h-14 w-14 items-center justify-center rounded-full border border-[#D4AF37]/60 bg-[#101010] shadow-[0_0_28px_rgba(212,175,55,0.25)]">
                        <MessagesSquare
                            className="h-6 w-6 text-[#D4AF37]"
                            strokeWidth={1.6}
                            aria-hidden="true"
                        />
                    </span>
                </div>

                <h2
                    id="contact-modal-heading"
                    data-testid="contact-modal-heading"
                    className="font-heading relative mt-3.5 text-center text-[24px] font-extrabold leading-tight tracking-normal md:text-[27px]"
                >
                    <span className="block text-white">WELCOME TO</span>{" "}
                    <span className="block text-gold-metallic">VIRAT WORKHUB</span>
                </h2>
                <p
                    id="contact-modal-subtext"
                    data-testid="contact-modal-subtext"
                    className="relative mt-1.5 text-center text-[13px] leading-snug text-[#B5B5B5] sm:text-sm"
                >
                    Connect with us and explore work opportunities.
                </p>
                <div
                    aria-hidden="true"
                    className="relative mx-auto mt-3 h-px w-20 bg-gradient-to-r from-transparent via-[#D4AF37] to-transparent"
                />

                <p
                    data-testid="telegram-eyebrow"
                    className="relative mb-2 mt-4 text-center text-[11px] font-bold tracking-normal text-[#8FD8FF]"
                >
                    ✦ JOIN OUR WORK COMMUNITY
                </p>

                <div className="relative flex flex-col gap-2">
                    <a
                        href={TELEGRAM_URL}
                        target="_blank"
                        rel="noopener noreferrer"
                        data-testid="contact-option-telegram"
                        aria-label="Connect on Telegram"
                        className="flex min-h-[64px] w-full items-center gap-3 rounded-2xl bg-gradient-to-r from-[#0088FF] to-[#0055FF] px-3.5 py-3 shadow-[0_0_26px_rgba(0,136,255,0.5)] ring-1 ring-white/15 transition-[box-shadow,transform,filter] duration-200 hover:-translate-y-0.5 hover:brightness-110 hover:shadow-[0_0_36px_rgba(0,136,255,0.7)] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#8FD8FF] active:translate-y-0"
                    >
                        <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full border border-white/30 bg-white/15 shadow-[0_0_14px_rgba(0,136,255,0.6)]">
                            <Send className="h-5 w-5 text-white" strokeWidth={2} aria-hidden="true" />
                        </span>
                        <span className="min-w-0 flex-1 text-left">
                            <span data-testid="telegram-button-label" className="block truncate text-[15px] font-extrabold leading-tight tracking-normal text-white">
                                CONNECT ON TELEGRAM
                            </span>
                            <span data-testid="telegram-button-supporting-text" className="mt-0.5 block text-[9.5px] font-semibold leading-snug tracking-normal text-white/90 sm:text-[10.5px]">
                                WORK UPDATES | NEW OPPORTUNITIES | GUIDANCE
                            </span>
                        </span>
                        <ArrowRight className="h-5 w-5 shrink-0 text-white" strokeWidth={2.2} aria-hidden="true" />
                    </a>

                    <a
                        href={WHATSAPP_URL}
                        target="_blank"
                        rel="noopener noreferrer"
                        data-testid="contact-option-whatsapp"
                        aria-label="Contact on WhatsApp"
                        className="flex min-h-[54px] w-full items-center gap-3 rounded-2xl bg-gradient-to-r from-[#00C853] to-[#00E676] px-3.5 py-2.5 shadow-[0_0_18px_rgba(0,230,118,0.35)] transition-[box-shadow,transform,filter] duration-200 hover:-translate-y-0.5 hover:brightness-105 hover:shadow-[0_0_28px_rgba(0,230,118,0.55)] focus:outline-none focus-visible:ring-2 focus-visible:ring-white active:translate-y-0"
                    >
                        <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full border border-white/30 bg-[#00A844]">
                            <WhatsAppIcon className="h-[18px] w-[18px] text-white" />
                        </span>
                        <span className="min-w-0 flex-1 text-left">
                            <span data-testid="whatsapp-button-label" className="block truncate text-[13px] font-extrabold leading-tight tracking-normal text-[#041F0E]">
                                CONTACT ON WHATSAPP
                            </span>
                            <span data-testid="whatsapp-button-supporting-text" className="mt-0.5 block text-[9px] font-semibold leading-snug tracking-normal text-[#063D1C] sm:text-[10px]">
                                CHAT WITH US | GET SUPPORT | QUICK RESPONSE
                            </span>
                        </span>
                        <ArrowRight className="h-[18px] w-[18px] shrink-0 text-[#041F0E]" strokeWidth={2.2} aria-hidden="true" />
                    </a>

                    <a
                        href={COMMUNITY_URL}
                        target="_blank"
                        rel="noopener noreferrer"
                        data-testid="contact-option-whatsapp-community"
                        aria-label="Join our WhatsApp Community"
                        className="flex min-h-[54px] w-full items-center gap-3 rounded-2xl border border-[#00E676]/60 bg-[#00C853]/10 px-3.5 py-2.5 shadow-[0_0_14px_rgba(0,230,118,0.2)] transition-[box-shadow,transform,border-color] duration-200 hover:-translate-y-0.5 hover:border-[#00E676] hover:shadow-[0_0_24px_rgba(0,230,118,0.38)] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#00E676] active:translate-y-0"
                    >
                        <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full border border-[#00E676]/60 bg-[#062A16]">
                            <Users className="h-[18px] w-[18px] text-[#00E676]" strokeWidth={1.8} aria-hidden="true" />
                        </span>
                        <span className="min-w-0 flex-1 text-left">
                            <span data-testid="community-button-label" className="block truncate text-[13px] font-extrabold leading-tight tracking-normal text-white">
                                JOIN OUR COMMUNITY
                            </span>
                            <span data-testid="community-button-supporting-text" className="mt-0.5 block text-[9px] font-semibold leading-snug tracking-normal text-[#00E676] sm:text-[10px]">
                                CONNECT | GET UPDATES | STAY INFORMED
                            </span>
                        </span>
                        <ArrowRight className="h-[18px] w-[18px] shrink-0 text-[#00E676]" strokeWidth={2.2} aria-hidden="true" />
                    </a>
                </div>
            </div>
        </div>
    );
}
